import { Actions, createLocalStorageStore } from '@bpd-library/utilities';
import { UserState, userStore } from '@store/user';

import {
    areaNewsletterApiCalls,
    AreaProjectNewsletterProps,
    ConstructionNumberNewsletterProps,
    ProjectPhaseNewsletterProps,
    PropertyTypeNewsletterProps,
} from './services/api-calls';
import {
    SubscriptionStoreAddress,
    SubscriptionStoreState,
    SubscriptionStoreUser,
} from './store.types';

const initialState: SubscriptionStoreState = {
    areaProject: false,
    projectPhases: [],
    propertyTypes: [],
    isUpdatedFromUserStore: false,
};

type SubscriptionStoreAction = 'init' | 'update' | 'updateBatch' | 'onLogin' | 'onLogout';

const actions: Actions<SubscriptionStoreAction, SubscriptionStoreState> = {
    init: (state: SubscriptionStoreState, payload: SubscriptionStoreState) => {
        state.user = payload.user;
        state.address = payload.address;
        state.areaProject = payload.areaProject;
        state.projectPhases = payload.projectPhases;
        state.propertyTypes = payload.propertyTypes;
        state.isUpdatedFromUserStore = payload.isUpdatedFromUserStore;

        return state;
    },
    updateBatch: (
        state: SubscriptionStoreState,
        {
            user,
            address,
            projectPhases,
            propertyTypes,
            areaProject,
        }: Partial<SubscriptionStoreState>,
    ) => {
        if (areaProject) state.areaProject = true;

        if (projectPhases?.length) {
            state.projectPhases = [...state.projectPhases, ...projectPhases];
        }

        if (propertyTypes?.length) {
            state.propertyTypes = [...state.propertyTypes, ...propertyTypes];
        }

        if (user) {
            state.user = {
                ...state.user,
                ...user,
            };
        }

        if (address) {
            state.address = {
                ...state.address,
                ...address,
            };
        }

        return state;
    },
    update: (
        state: SubscriptionStoreState,
        {
            areaInfo,
            userInfo,
            address,
        }: AreaProjectNewsletterProps &
            ProjectPhaseNewsletterProps &
            PropertyTypeNewsletterProps &
            ConstructionNumberNewsletterProps,
    ) => {
        if (areaInfo) {
            const { areaProjectId, projectPhaseId, propertyTypeId } = areaInfo;

            /*
                If one of the following id's is included, the user will be subscribed to one of those newsletters
            */
            if (areaProjectId) state.areaProject = true;
            if (propertyTypeId) {
                state.propertyTypes = [...state.propertyTypes, propertyTypeId];
            } else if (projectPhaseId) {
                state.projectPhases = [...state.projectPhases, projectPhaseId];
            }
        }

        if (userInfo) {
            state.user = {
                ...state.user,
                ...userInfo,
            };
        }

        if (address) {
            state.address = {
                ...state.address,
                ...address,
            };
        }

        return state;
    },
    onLogin: (state: SubscriptionStoreState, userState: UserState) => {
        if (userState.info) {
            const {
                email,
                initials,
                firstName,
                lastName,
                gender,
                subscribedToAreaNewsletter,
                zipcode,
                street,
                houseNumber,
                houseNumberSuffix,
                city,
                phoneNumber,
                country,
            } = userState.info;

            state.address = {
                city,
                zipcode,
                street,
                houseNumber,
                houseNumberAddition: houseNumberSuffix,
                phoneNumber,
                country,
            };

            state.user = {
                email,
                initials,
                firstName,
                lastName,
                gender,
            };

            state.areaProject = !!subscribedToAreaNewsletter;
            state.projectPhases = userState.projects?.map(({ Id }) => Id) ?? [];
            state.propertyTypes = userState.propertyTypes?.map(({ Id }) => Id) ?? [];
            state.isUpdatedFromUserStore = true;
        }

        return state;
    },
    onLogout: (state: SubscriptionStoreState) => {
        state.projectPhases = [];
        state.propertyTypes = [];
        state.areaProject = false;
        state.address = undefined;
        state.user = undefined;
        state.isUpdatedFromUserStore = false;

        return state;
    },
};

export const subscriptionStore = createLocalStorageStore<
    SubscriptionStoreState,
    SubscriptionStoreAction
>({
    key: 'subscription',
    initialState,
    actions,
    expireInDays: 30,
    initAction: 'init',
});

/*
    When logging in, fill the store with the available data
    When logging out, empty the store
*/
userStore.subscribe(
    (userState) => {
        if (userState.loggedIn) {
            // Only update it from the user store once
            // on a new page view, new user api call will be done
            // which is not up to date yet with the latest newsletter subscription
            if (!getSubscriptionState().isUpdatedFromUserStore)
                subscriptionStore.dispatch('onLogin', userState);
        } else {
            subscriptionStore.dispatch('onLogout');
        }
    },
    ['loggedIn'],
);

export const subscribeToAreaProject = async (props: AreaProjectNewsletterProps) => {
    try {
        await areaNewsletterApiCalls.areaProject(props);
        subscriptionStore.dispatch('update', props);
    } catch {}
};

export const subscribeToProjectPhase = async (props: ProjectPhaseNewsletterProps) => {
    try {
        await areaNewsletterApiCalls.projectPhase(props);
        subscriptionStore.dispatch('update', props);
    } catch {}
};

export const subscribeToAreaProjectAndProjectPhases = async (
    projectPhasesProps: ProjectPhaseNewsletterProps[],
    areaProjectProps?: AreaProjectNewsletterProps,
) => {
    const stagedUpdate: Partial<SubscriptionStoreState> = {};
    try {
        if (areaProjectProps) {
            await areaNewsletterApiCalls.areaProject(areaProjectProps);
            stagedUpdate.areaProject = true;
            stagedUpdate.user = {
                ...areaProjectProps.userInfo,
            };
        }

        stagedUpdate.address = projectPhasesProps[0].address;
        stagedUpdate.projectPhases = [];

        for (const props of projectPhasesProps) {
            await areaNewsletterApiCalls.projectPhase(props);
            stagedUpdate.projectPhases.push(props.areaInfo.projectPhaseId);
        }

        subscriptionStore.dispatch('updateBatch', stagedUpdate);
    } catch {}
};

export const subscribeToPropertyType = async (props: PropertyTypeNewsletterProps) => {
    try {
        await areaNewsletterApiCalls.propertyType(props);
        subscriptionStore.dispatch('update', props);
    } catch {}
};

export const subscribeToPropertyTypes = async (
    propertyTypesProps: PropertyTypeNewsletterProps[],
) => {
    const stagedUpdate: Partial<SubscriptionStoreState> = {};
    try {
        stagedUpdate.propertyTypes = [];

        for (const props of propertyTypesProps) {
            await areaNewsletterApiCalls.propertyType(props);
            stagedUpdate.propertyTypes.push(props.areaInfo.propertyTypeId);
        }

        subscriptionStore.dispatch('updateBatch', stagedUpdate);
    } catch {}
};

export const subscribeToConstructionNumber = async (props: ConstructionNumberNewsletterProps) => {
    try {
        await areaNewsletterApiCalls.constructionNumber(props);
        subscriptionStore.dispatch('update', props);
    } catch {}
};

export const isSubscribedToAreaProject = () => subscriptionStore.getState().areaProject;

export const isSubscribedToPropertyType = (propertyTypeId: string) =>
    subscriptionStore.getState().propertyTypes.includes(propertyTypeId);

export const isSubscribedToProjectPhase = (projectPhaseId: string) =>
    subscriptionStore.getState().projectPhases.includes(projectPhaseId);

export const getSubscriptionUser = () => subscriptionStore.getState().user;

export const getSubscriptionAddress = () => subscriptionStore.getState().address;

export const getSubscriptionState = () => subscriptionStore.getState();

export const isStoreUserDataComplete = (user?: SubscriptionStoreUser) => {
    if (!user) return false;
    const { email, firstName, lastName, gender } = user;
    return !!(email && firstName && lastName && gender);
};

export const isStoreAddressDataComplete = (address?: SubscriptionStoreAddress) => {
    if (!address) return false;
    const { zipcode, phoneNumber, street, houseNumber, city } = address;
    return !!(zipcode && street && houseNumber && city && phoneNumber);
};

export const isSubscriptionFormRequired = (): { area: boolean; project: boolean } => {
    const userInfo = getSubscriptionUser();
    const addressInfo = getSubscriptionAddress();

    const hasUserInfo = isStoreUserDataComplete(userInfo);
    const hasAddressInfo = isStoreAddressDataComplete(addressInfo);

    return { area: !hasUserInfo, project: !(hasUserInfo && hasAddressInfo) };
};
